import { Box } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import colors from 'styles/colors'
import { getSizeString } from 'utils/formatText'
import {
  getMaxima,
  prepareRadarGraphData,
  processData
} from 'utils/radarGraphHelper'
import {
  VictoryPolarAxis,
  VictoryArea,
  VictoryTheme,
  VictoryScatter,
  VictoryGroup,
  VictoryChart,
  VictoryLabel,
  VictoryContainer,
  VictoryLine
} from 'victory'
import GraphLabel from 'components/atoms/GraphLabel'
import AssessmentBehaviourPopup from 'components/organisms/assessments/AssessmentBehaviourPopup'
import { MD_DESKTOP } from 'utils/constants'

export const radarGraphLocationString = 'RadarGraph'

const RadarGraphNew = ({
  assessmentData,
  size = 500,
  disableBehaviorClick = false,
  showScoreLabel = false,
  disableScroll = false,
  styles = {}
}) => {
  const ref = useRef()
  const svgRef = useRef(null)
  const [rawRatings, setRawRatings] = useState([])
  const [graphData, setGraphData] = useState([])
  const [maximaData, setMaximaData] = useState([])
  const [chartLabels, setChartLabels] = useState([])
  const [openDetails, setOpenDetails] = useState(false)
  const [selectedBehaviour, setSelectedBehaviour] = useState(null)

  const scrollToGraph =
    window.history.state?.graphData?.id === assessmentData.id && !disableScroll

  const handleBehaviorNavigation = behavior => {
    if (!disableBehaviorClick) {
      setOpenDetails(true)
      setSelectedBehaviour(behavior)
    }
  }

  useEffect(() => {
    if (assessmentData?.radarChartData) {
      const characterData = prepareRadarGraphData(assessmentData.radarChartData)
      setRawRatings(characterData)
      setGraphData(processData(characterData))
      const maxima = getMaxima(characterData)
      setMaximaData(maxima)
      if (scrollToGraph && ref.current) {
        ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }
  }, [assessmentData, scrollToGraph])

  const HandleLabelRender = ({ text, ...props }) => {
    const newChartLabels = useRef([...chartLabels])
    useEffect(() => {
      const exists = newChartLabels.current.some(
        lbl => lbl.text.join('') === text.join('')
      )
      if (!exists) {
        newChartLabels.current.push({ text, ...props })
        setChartLabels([...newChartLabels.current])
      }
    }, [text, props])

    return null // Don't render inside SVG
  }

  return (
    <Box
      display='flex'
      justifyContent='center'
      alignContent='center'
      position='relative'
      sx={{
        minWidth: getSizeString(size),
        height: getSizeString(size),
        [MD_DESKTOP]: {
          minWidth: getSizeString(size - 120),
          height: getSizeString(size - 120)
        }
      }}
      style={{
        marginTop: 40,
        marginBottom: 40,
        ...styles
      }}>
      {Object.keys(maximaData).length > 0 ? (
        <VictoryChart
          polar
          theme={VictoryTheme.material}
          domain={{ y: [0, 1] }}
          containerComponent={
            <VictoryContainer
              ref={node => {
                if (node) {
                  const svg = node.containerRef?.querySelector('g')
                  svgRef.current = svg.querySelector('path') // Get the internal SVG reference
                }
              }}
            />
          }>
          {/* Outer MidnightTeal300 circle */}
          <VictoryPolarAxis
            tickFormat={() => ''}
            style={{
              axis: {
                strokeWidth: 4,
                opacity: 1,
                stroke: colors.MidnightTeal300
              },
              ticks: {
                stroke: colors.MidnightTeal400,
                strokeWidth: 1,
                size: 12
              }
            }}
          />

          {/* All the labels */}
          {Object.entries(maximaData).map(([key, value], index) => {
            const selfScore = rawRatings[0][key]
            const otherScore = rawRatings[1][key]
            const angle = (index * 2 * Math.PI) / Object.keys(maximaData).length

            return (
              <VictoryPolarAxis
                key={`vPA ${index}`}
                dependentAxis
                style={{
                  axis: {
                    stroke: colors.Grey250,
                    strokeWidth: 1
                  },
                  grid: {
                    stroke: colors.Grey250,
                    strokeWidth: 1,
                    strokeDasharray: [8, 10]
                  }
                }}
                label={key.split(/(?<=[ -])/)}
                labelPlacement='vertical'
                axisValue={index + 1}
                tickFormat={t => t * 5}
                tickValues={[0.2, 0.4, 0.6, 0.8]}
                tickLabelComponent={
                  <VictoryLabel
                    style={{
                      fill: colors.Grey300,
                      fontSize: 13,
                      fontFamily: 'Inter'
                    }}
                    labelPlacement='vertical'
                  />
                }
                axisLabelComponent={
                  <HandleLabelRender
                    attrKey={key}
                    selfScore={selfScore}
                    otherScore={otherScore}
                    angle={angle}
                    handleBehaviorNavigation={handleBehaviorNavigation}
                    assessmentData={assessmentData}
                    showScoreLabel={showScoreLabel}
                  />
                }
              />
            )
          })}

          {/* Polygon-fill area */}
          <VictoryGroup
            colorScale={[colors.Teal400, colors.MidnightTeal400, 'transparent']}
            style={{ data: { fillOpacity: 0.15, strokeWidth: 2 } }}>
            {graphData.map((data, i) => {
              return <VictoryArea key={`vPA ${i}`} data={data}></VictoryArea>
            })}
          </VictoryGroup>

          {/* Filled dots on vertices */}
          {graphData[0].map((value, index) => {
            return (
              <VictoryScatter
                key={`vPA ${index}`}
                size={6}
                data={[{ x: value.x, y: value.y }]}
                style={{
                  data: { fill: colors.Teal400, opacity: 1 }
                }}
              />
            )
          })}

          {/* Extended lines */}
          {graphData[0].map((value, index) => {
            return (
              <VictoryLine
                key={`extended-axis-${index}`}
                data={[
                  { x: value.x, y: value.y + 0.05 }, // Start from center
                  { x: index + 1, y: 1 } // Extend beyond 1
                ]}
                style={{
                  data: {
                    stroke: colors.MidnightTeal300,
                    strokeWidth: 1,
                    vectorEffect: 'non-scaling-stroke'
                  }
                }}
              />
            )
          })}

          {Object.keys(assessmentData?.radarChartData?.others).length > 0
            ? graphData[1].map((value, index) => {
                return (
                  <VictoryScatter
                    key={`vPA-1 ${index}`}
                    size={6}
                    data={[{ x: value.x, y: value.y }]}
                    style={{
                      data: { fill: colors.MidnightTeal400, opacity: 1 }
                    }}
                  />
                )
              })
            : null}
        </VictoryChart>
      ) : null}

      {/* Render Custom labels */}
      {chartLabels?.map((label, index) => (
        <GraphLabel
          key={index}
          {...label}
          svgRef={svgRef}
          disableBehaviorClick={disableBehaviorClick}
        />
      ))}

      <AssessmentBehaviourPopup
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        assessmentData={assessmentData}
        behaviour={selectedBehaviour}
      />
    </Box>
  )
}

export default RadarGraphNew
